import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { AaiSkill, ControlEvent } from '../../types/aai';
import { Action, Chip, Drawer, Field, Input, Kicker, Select } from '../../components/aa';
import { BLOCKERS, plural, slotOptions } from './champion';

/*
 * The AI Champion's three adoption actions. Each is a recorded control action
 * that names a team, a blocker or a capability, never a person, and reaches the
 * group that can act on it: the program team, the Owner, the platform, or the
 * team's own lead. The drawer shows what it is about to record, records it,
 * then reads back as completed.
 */

interface Option { value: string; label: string }

interface FieldSpec {
  id: string;
  label: string;
  kind: 'select' | 'input';
  value: string;
  onChange: (value: string) => void;
  options?: Option[];
  placeholder?: string;
}

interface Tile { label: string; value: string }

const Tiles: React.FC<{ tiles: Tile[] }> = ({ tiles }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {tiles.map((t) => (
      <div key={t.label} className="rounded-card border border-line bg-canvas-inset px-5 py-4">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{t.label}</div>
        <div className="mt-1.5 text-[15px] font-bold text-ink">{t.value}</div>
      </div>
    ))}
  </div>
);

const ActionDrawer: React.FC<{
  count: string;
  title: string;
  sub: string;
  doneSub: string;
  tiles: Tile[];
  fields: FieldSpec[];
  submitLabel: string;
  busy: boolean;
  done: boolean;
  onSubmit: () => void;
  onClose: () => void;
}> = ({ count, title, sub, doneSub, tiles, fields, submitLabel, busy, done, onSubmit, onClose }) => {
  let badge = <Chip>{count}</Chip>;
  if (done) badge = <Chip tone="ok">Completed</Chip>;
  let subtitle = sub;
  if (done) subtitle = doneSub;
  let footer = (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>
      <Action size="lg" onClick={onSubmit} disabled={busy} reason="Recording">{submitLabel}</Action>
    </div>
  );
  if (done) footer = <div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>;
  return (
    <Drawer onClose={onClose} kicker={<Kicker>AI Adoption</Kicker>} badge={badge} title={title} sub={subtitle} footer={footer}>
      <Tiles tiles={tiles} />
      {!done && (
        <div className="mt-6 space-y-5">
          {fields.map((f) => (
            <Field key={f.id} label={f.label} htmlFor={f.id}>
              {f.kind === 'select' && (
                <Select id={f.id} value={f.value} onChange={(e) => f.onChange(e.target.value)}>
                  {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
              )}
              {f.kind === 'input' && (
                <Input id={f.id} value={f.value} onChange={(e) => f.onChange(e.target.value)} placeholder={f.placeholder} />
              )}
            </Field>
          ))}
        </div>
      )}
    </Drawer>
  );
};

const useRecord = (onRecorded: () => void) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const run = async (body: { kind: string; subject: string; status: string; payload: Record<string, unknown> }, message: string) => {
    setBusy(true);
    try {
      await aaiApi.recordControlEvent(body);
      setDone(true);
      toast.success(message);
      onRecorded();
    } catch {
      toast.error('That did not record.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, done, run };
};

const toOptions = (values: string[]): Option[] => values.map((v) => ({ value: v, label: v }));

const payloadString = (e: ControlEvent, key: string): string => {
  const value = (e.payload || {})[key];
  if (typeof value === 'string') return value;
  return '';
};

/** The blocker named most often across recorded support and blocker actions. */
export const mostCommonBlocker = (events: ControlEvent[]): string | null => {
  const counts = new Map<string, number>();
  events.forEach((e) => {
    const blocker = payloadString(e, 'blocker');
    if (!blocker) return;
    counts.set(blocker, (counts.get(blocker) || 0) + 1);
  });
  let best: string | null = null;
  let bestCount = 0;
  counts.forEach((n, blocker) => {
    if (n <= bestCount) return;
    best = blocker;
    bestCount = n;
  });
  return best;
};

const RESPONSES = ['Targeted office hour', 'Group clinic', 'Paired first run', 'Written walkthrough'];

export const PlanSupportDrawer: React.FC<{
  teams: string[];
  priority: string[];
  initialTeam: string | null;
  onRecorded: () => void;
  onClose: () => void;
}> = ({ teams, priority, initialTeam, onRecorded, onClose }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const [team, setTeam] = useState(initialTeam || priority[0] || teams[0] || '');
  const [blocker, setBlocker] = useState(BLOCKERS[0]);
  const [response, setResponse] = useState(RESPONSES[0]);
  const [owner, setOwner] = useState(localStorage.getItem('user_name') || '');

  const submit = () => {
    if (!team) {
      toast.error('Choose the team the support is for.');
      return;
    }
    if (!owner.trim()) {
      toast.error('Name the support owner. The response needs one accountable person.');
      return;
    }
    run({
      kind: 'support_request',
      subject: `${blocker} · ${team}`,
      status: 'planned',
      payload: {
        department: team,
        blocker,
        response,
        owner: owner.trim(),
        path: `team-${team}`,
        note: `${response} for ${team} roles. Support owner: ${owner.trim()}. Blocker: ${blocker}.`,
      },
    }, 'Support planned. The program team has the request.');
  };

  return (
    <ActionDrawer
      count={plural(priority.length, 'team', 'teams')}
      title="Plan support for one team"
      sub="Turn one shared blocker into a named, time-bound response."
      doneSub="Targeted team support planned"
      tiles={[
        { label: 'Teams needing help', value: `${priority.length}` },
        { label: 'Highest priority', value: priority[0] || 'None' },
        { label: 'Private responses', value: 'Excluded' },
      ]}
      fields={[
        { id: 'plan-team', label: 'Team', kind: 'select', value: team, onChange: setTeam, options: toOptions(teams) },
        { id: 'plan-blocker', label: 'Shared blocker', kind: 'select', value: blocker, onChange: setBlocker, options: toOptions(BLOCKERS) },
        { id: 'plan-response', label: 'Support response', kind: 'select', value: response, onChange: setResponse, options: toOptions(RESPONSES) },
        { id: 'plan-owner', label: 'Support owner', kind: 'input', value: owner, onChange: setOwner, placeholder: 'Who runs the response' },
      ]}
      submitLabel="Create support plan"
      busy={busy}
      done={done}
      onSubmit={submit}
      onClose={onClose}
    />
  );
};

const ROUTES: Option[] = [
  { value: 'platform', label: 'AI Platform Operations' },
  { value: 'owner', label: 'Owner' },
  { value: 'program', label: 'Program team' },
];

const RESPONSE_BY = ['Within 3 working days', 'Within 1 week', 'At the next program review'];

export const AssignBlockerDrawer: React.FC<{
  events: ControlEvent[];
  onRecorded: () => void;
  onClose: () => void;
}> = ({ events, onRecorded, onClose }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const open = events.filter((e) => e.kind === 'blocker_assignment' && (e.status === 'assigned' || e.status === 'acknowledged'));
  const common = mostCommonBlocker(events) || BLOCKERS[0];
  const [blocker, setBlocker] = useState(common);
  const [route, setRoute] = useState(ROUTES[0].value);
  const [responseBy, setResponseBy] = useState(RESPONSE_BY[0]);
  const routeLabel = ROUTES.find((r) => r.value === route)?.label || route;

  const submit = () => run({
    kind: 'blocker_assignment',
    subject: blocker,
    status: 'assigned',
    payload: {
      blocker,
      route_to: route,
      route_label: routeLabel,
      response_by: responseBy,
      note: `Aggregate blocker routed to ${routeLabel}. Response requested ${responseBy.toLowerCase()}. No individual detail is included.`,
    },
  }, `Blocker assigned to ${routeLabel}.`);

  return (
    <ActionDrawer
      count={`${open.length} open`}
      title="Assign a shared blocker"
      sub="Route aggregate feedback to the person who can remove the constraint."
      doneSub={`Blocker routed to ${routeLabel}`}
      tiles={[
        { label: 'Open blockers', value: `${open.length}` },
        { label: 'Most common', value: common },
        { label: 'Individual detail', value: 'Not available' },
      ]}
      fields={[
        { id: 'blocker-name', label: 'Blocker', kind: 'select', value: blocker, onChange: setBlocker, options: toOptions(BLOCKERS) },
        { id: 'blocker-route', label: 'Route to', kind: 'select', value: route, onChange: setRoute, options: ROUTES },
        { id: 'blocker-by', label: 'Response by', kind: 'select', value: responseBy, onChange: setResponseBy, options: toOptions(RESPONSE_BY) },
      ]}
      submitLabel="Assign blocker"
      busy={busy}
      done={done}
      onSubmit={submit}
      onClose={onClose}
    />
  );
};

export const DemonstrationDrawer: React.FC<{
  skills: AaiSkill[];
  teams: string[];
  onRecorded: () => void;
  onClose: () => void;
}> = ({ skills, teams, onRecorded, onClose }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const slots = slotOptions();
  const [skillId, setSkillId] = useState(skills[0]?.id || '');
  const [team, setTeam] = useState(teams[0] || '');
  const [slotAt, setSlotAt] = useState(slots[0]?.at || '');
  const slot = slots.find((s) => s.at === slotAt)?.label || '';
  const owner = localStorage.getItem('user_name') || localStorage.getItem('user_email') || 'You';
  const skill = skills.find((s) => s.id === skillId) || null;

  let ready = 'None published yet';
  if (skills.length > 0) ready = skills.slice(0, 2).map((s) => s.name).join(' · ');

  const submit = () => {
    if (!skill) {
      toast.error('A demonstration needs a published Quick Win.');
      return;
    }
    if (!team) {
      toast.error('Choose the team that will see it.');
      return;
    }
    run({
      kind: 'demonstration',
      subject: `${skill.name} · ${team}`,
      status: 'scheduled',
      payload: {
        skill_id: skill.id,
        skill: skill.name,
        department: team,
        slot,
        slot_at: slotAt,
        owner,
        note: `Validated workflow demonstration of ${skill.name} for ${team}, ${slot}. No production actions during the demonstration.`,
      },
    }, 'Demonstration scheduled. The program team and the team lead have it.');
  };

  return (
    <ActionDrawer
      count={`${skills.length} ready`}
      title="Schedule a validated workflow demonstration"
      sub="Show one approved Quick Win in the team's real operating context."
      doneSub="Demonstration scheduled"
      tiles={[
        { label: 'Ready demonstrations', value: ready },
        { label: 'Production actions', value: 'None during demonstration' },
        { label: 'Owner', value: owner },
      ]}
      fields={[
        { id: 'demo-skill', label: 'Approved workflow', kind: 'select', value: skillId, onChange: setSkillId, options: skills.map((s) => ({ value: s.id, label: s.name })) },
        { id: 'demo-team', label: 'Team', kind: 'select', value: team, onChange: setTeam, options: toOptions(teams) },
        { id: 'demo-slot', label: 'Session', kind: 'select', value: slotAt, onChange: setSlotAt, options: slots.map((s) => ({ value: s.at, label: s.label })) },
      ]}
      submitLabel="Schedule demonstration"
      busy={busy}
      done={done}
      onSubmit={submit}
      onClose={onClose}
    />
  );
};
